import type React from 'react';
import { IS_WOA, IS_INTLGAME, IS_GLOBAL } from '@home/_config/config';
import { ESkillSourceType } from '../types';
import type { ISkillData, TSkillPackageVersion } from '../types';
import type { ISkillDisplayViewModel } from '../types/skillDetail';
import { WOA_HOSTNAME_SUFFIX } from '../constants/skillDetail';
import OfficialSkillAvatar from '@/pages/aigc/assets/svg/databrain-logo.svg';
import { mathFormat } from '@/pages/home/utils/search';

/**
 * 按当前部署环境解析 Skill 包 version。
 * Global → global；WOA → woa；intlgame 非 Global → service；其余默认 woa。
 */
export const getSkillPackageVersion = (): TSkillPackageVersion => {
  if (IS_GLOBAL) {
    return 'global';
  }
  // if (IS_WOA) {
  //   return 'woa';
  // }
  // if (IS_INTLGAME) {
  //   return 'service';
  // }
  return 'woa';
};

/**
 * 解析 Skill 展示名称
 * @param skill - Skill 原始数据
 * @param isEN - 是否英文环境
 * @returns 用于 UI 的展示名称
 */
export const getSkillDisplayName = (skill: ISkillData, isEN: boolean): string => {
  if (isEN) {
    return skill.display_name_en?.trim() || skill.skill_name || '';
  }
  return (
    skill.display_name_cn?.trim()
    || skill.skill_name_cn
    || skill.skill_name
    || ''
  );
};

/**
 * 解析 Skill 描述文案
 * @param skill - Skill 原始数据
 * @param isEN - 是否英文环境
 */
export const getSkillDescription = (skill: ISkillData, isEN: boolean): string =>
  (isEN ? skill.description_en : skill.description) || '';

/**
 * 解析分类标签行内样式
 * @param skill - Skill 原始数据
 */
export const getSkillCategoryTagStyle = (
  skill: ISkillData,
): React.CSSProperties | undefined => {
  const textColor = skill.category_text_color?.trim();
  const backgroundColor = skill.category_text_color_background?.trim();

  if (!textColor && !backgroundColor) {
    return undefined;
  }

  return {
    ...(textColor ? { color: textColor } : {}),
    ...(backgroundColor ? { background: backgroundColor } : {}),
  };
};

/**
 * 解析作者头像地址
 * @param skill - Skill 原始数据
 */
export const getSkillAuthorAvatarSrc = (skill: ISkillData): string => {
  const fromApi = skill.author_avatar?.trim();
  if (fromApi) {
    return fromApi;
  }
  if (skill.source_type === ESkillSourceType.OFFICIAL) {
    return OfficialSkillAvatar;
  }
  return skill.profile_pic || '';
};

/**
 * 是否展示 Knot 跳转入口
 * @param skill - Skill 原始数据
 */
export const shouldShowSkillKnot = (skill: ISkillData): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  return (
    window.location.hostname.endsWith(WOA_HOSTNAME_SUFFIX)
    && skill.show_knot_btn === 1
    && Boolean(skill.knot_link?.trim())
  );
};

/**
 * 格式化 Skill 使用量
 * @param dialogCount - 对话次数
 */
export const formatSkillDialogCount = (dialogCount?: number): string => {
  if (dialogCount === undefined || dialogCount === null) {
    return '—';
  }
  const formatted = mathFormat(dialogCount);
  return formatted === '-' ? '0' : String(formatted);
};

/**
 * 格式化 Skill 更新时间
 * @param updateTime - 接口返回的更新时间
 */
export const formatSkillUpdateTime = (updateTime?: string): string => {
  const raw = updateTime?.trim();
  if (!raw) {
    return '—';
  }
  return raw.replace('T', ' ');
};

/**
 * 构建 Skill 详情页展示视图模型
 * @param skill - Skill 原始数据
 * @param isEN - 是否英文环境
 */
export const buildSkillDisplayViewModel = (
  skill: ISkillData,
  isEN: boolean,
): ISkillDisplayViewModel => ({
  displayName: getSkillDisplayName(skill, isEN),
  description: getSkillDescription(skill, isEN),
  sourceText: isEN ? skill.source_label_en : skill.source_label,
  categoryText: isEN ? (skill.category_en || skill.category) : skill.category,
  authorDisplayName: skill.author_name?.trim() || skill.user_name || '—',
  authorAvatarSrc: getSkillAuthorAvatarSrc(skill),
  categoryTagStyle: getSkillCategoryTagStyle(skill),
  updateTimeText: formatSkillUpdateTime(skill.update_time),
  dialogCountText: formatSkillDialogCount(skill.dialog_count),
  showKnot: shouldShowSkillKnot(skill),
  knotLink: skill.knot_link?.trim() || '',
});
